import type {
  Question,
  QuestionContent,
  SubQuestion,
  SubQuestionTranslation,
} from 'src/types/helios/Question';

/**
 * Minimal `localizedContent` shape both `Question.localizedContent`
 * (from the full per-question fetch) and
 * `QuestionWithResponse.questionDetails.localizedContent` (from the
 * audit-summary payload) structurally satisfy. Kept intentionally
 * narrow — the callers of {@link getAvailableQuestionLanguages} only
 * need to walk the primary language + translation keys.
 */
interface LocalizedContentLike {
  language?: string | undefined;
  translations?: Record<string, unknown> | undefined;
}

/**
 * Ordered, deduped list of BCP-47 locales that carry content on a
 * single question — the primary `localizedContent.language` first,
 * then every locale key present in `localizedContent.translations`.
 *
 * Drives the per-question language picker's `availableLocales` prop
 * (rendered inside `PerformAuditQuestionCard`). A question whose
 * `translations` map is empty (or missing) returns a single-entry
 * array — the caller then hides the picker on that question because
 * a one-option picker is a no-op (no locale to switch TO).
 *
 * The helper is per-question (not per-audit) so a partially-translated
 * template surfaces the picker only on questions that actually have
 * translations, and hides it on questions that don't. This matches the
 * user's expectation that "the picker doesn't show up if there's no
 * translation to switch to."
 */
export function getAvailableQuestionLanguages(
  localizedContent: LocalizedContentLike | undefined,
): string[] {
  if (!localizedContent) return [];
  const seen = new Set<string>();
  const ordered: string[] = [];
  const push = (locale: string | undefined): void => {
    if (locale && !seen.has(locale)) {
      seen.add(locale);
      ordered.push(locale);
    }
  };
  push(localizedContent.language);
  if (localizedContent.translations) {
    for (const locale of Object.keys(localizedContent.translations)) {
      push(locale);
    }
  }
  return ordered;
}

/**
 * Returns the question content (title, questionText, sub-questions) for
 * the given language. Fallback chain: localizedContent.content (server-
 * localized) → localizedContent translations → localeTranslationContentMap
 * → default language content.
 *
 * Consumed by both the task question-info expander and the mobile
 * perform-audit page — hence shared/domain rather than a feature.
 */
export function getLocalizedQuestionContent(
  question: Question | undefined,
  language: string | undefined,
): QuestionContent | undefined {
  if (!question) return undefined;
  const defaultContent: QuestionContent = question.localizedContent?.content ?? {
    ...question.defaultLanguageContent,
    // Conditional spread keeps the optional prop absent rather than
    // explicitly undefined (exactOptionalPropertyTypes).
    ...(question.subQuestions ? { subQuestions: question.subQuestions } : {}),
  };
  if (!language) return defaultContent;

  const translated =
    // eslint-disable-next-line security/detect-object-injection -- locale code from user preferences, not user input
    question.localizedContent?.translations?.[language]?.content;
  if (translated) return translated;

  // eslint-disable-next-line security/detect-object-injection -- locale code from user preferences, not user input
  const mapEntry = question.localeTranslationContentMap?.[language];
  const mapTranslation = mapEntry?.questionTranslation;
  if (mapTranslation) {
    // Sub-question translations for this locale live in a sibling
    // `subQuestionsTranslationMap` keyed by `subQuestionId`. Merge
    // per-sub-question (title / instruction / taskDetails swap;
    // structural fields — orderIndex, mediaIdsMap, links, score,
    // require* — survive) so switching locale swaps sub-question
    // copy alongside the parent title, matching the auditor's
    // expectation that the whole question surface reads in the
    // picked language.
    //
    // Sub-questions without a matching entry in the map fall back
    // to the default-language sub-question — partial translations
    // stay usable rather than presenting missing bodies.
    const subTranslationMap = mapEntry?.subQuestionsTranslationMap;
    const translatedSubQuestions = mergeSubQuestionTranslations(
      defaultContent.subQuestions,
      subTranslationMap,
    );
    return {
      ...mapTranslation,
      ...(translatedSubQuestions ? { subQuestions: translatedSubQuestions } : {}),
    };
  }
  return defaultContent;
}

/**
 * Apply per-sub-question translation entries (matched by
 * `subQuestionId`) onto a default-language sub-question list.
 * Returns a new array — never mutates the input.
 *
 * Non-translatable structural fields (`orderIndex`, `mediaIdsMap`,
 * `links`, `score`, `require*OnFail/Pass`) are preserved verbatim so
 * the merged sub-question stays behaviourally identical to its
 * default-language counterpart; only the auditor-facing copy is
 * swapped. Sub-questions without a matching translation entry are
 * returned unchanged so partial translations stay usable.
 *
 * Exposed only inside `getLocalizedQuestionContent` — the merge is
 * an implementation detail of the Path-B branch.
 */
function mergeSubQuestionTranslations(
  defaultSubQuestions: SubQuestion[] | undefined,
  subTranslationMap: Record<string, SubQuestionTranslation> | undefined,
): SubQuestion[] | undefined {
  if (!defaultSubQuestions) return undefined;
  if (!subTranslationMap) return defaultSubQuestions;
  return defaultSubQuestions.map((sq) => {
    if (!sq.subQuestionId) return sq;

    const t = subTranslationMap[sq.subQuestionId];
    if (!t) return sq;
    return {
      ...sq,
      title: t.title,
      instruction: t.instruction,
      ...(t.taskDetails !== undefined ? { taskDetails: t.taskDetails } : {}),
    };
  });
}
